import { FriendshipWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, PatternBackground, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, Text, View } from 'react-native';
import { createStyles } from './FriendRequestsScreen.styles';

export interface FriendRequestsScreenProps {
  requests: FriendshipWithUser[];
  loading: boolean;
  loadingMore: boolean;
  onAccept: (friendshipId: string) => void;
  onDecline: (friendshipId: string) => void;
  onUserPress: (userId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const FriendRequestsScreen = ({
  requests,
  loading,
  loadingMore,
  onAccept,
  onDecline,
  onUserPress,
  onRefresh,
  onLoadMore,
  colors,
}: FriendRequestsScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <PatternBackground>
        <LoadingSpinner />
      </PatternBackground>
    );
  }

  const renderRequestItem = ({ item }: { item: FriendshipWithUser }) => {
    return (
      <UserCard
        userId={item.requesterId}
        userName={item.userName}
        userEmail={item.userEmail}
        userAvatarUrl={item.userAvatarUrl}
        onPress={() => onUserPress(item.requesterId)}
        actions={
          <View style={styles.actions}>
            <Button
              title={t('social.acceptRequest')}
              onPress={() => onAccept(item.id)}
              variant="primary"
              size="small"
              colors={colors}
            />
            <Button
              title={t('social.declineRequest')}
              onPress={() => onDecline(item.id)}
              variant="secondary"
              size="small"
              colors={colors}
            />
          </View>
        }
        colors={colors}
      />
    );
  };

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <Text style={[styles.count, { color: colors.textSecondary }]}>
            {requests.length === 1
              ? t('social.requestCountSingular', { count: requests.length })
              : t('social.requestCount', { count: requests.length })}
          </Text>
          <Ionicons name="person-add" size={24} color={colors.primary} />
        </View>
      </View>

      <FlatList
        data={requests}
        renderItem={renderRequestItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="person-add-outline" size={52} color={colors.textTertiary} />}
            title={t('social.noPendingRequests')}
            subtitle={t('social.whenReceiveRequestsAppear')}
            colors={colors}
          />
        }
      />
    </PatternBackground>
  );
};
